// Staff authentication, matching CEL-App and evie-dashboard exactly.
//
// Same endpoint (Longview-Api POST /login), same response shape, same
// localStorage key, same 24-hour expiry. The token is the `x-api-key` value for
// every other Longview-Api call.
//
// This gates the *inspector* view only. The guest view is deliberately
// unauthenticated: a customer joining an inspection call should not need an
// account, and the call ID in their link is what admits them.

import { endpoints } from '../config/endpoints';

// The same key CEL-App uses. These are separate origins so nothing is actually
// shared, but keeping the name identical means one thing to look for when
// debugging a session across the estate.
const SESSION_KEY = 'eh_session';

export type SessionUser = {
  id: number;
  email: string;
  name: string;
  role: string;
};

export type Session = {
  token: string;
  orgId: string;
  expiresAt: string;
  user: SessionUser;
};

function isExpired(session: Session | null): boolean {
  if (!session?.expiresAt) return true;
  return new Date(session.expiresAt) <= new Date();
}

/** The stored session, or null if absent, unreadable, or expired. */
export function getSession(): Session | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (!session?.token || isExpired(session)) return null;
    return session;
  } catch {
    // Storage disabled, or a blob written by an older build. Treated as signed
    // out rather than allowed to break the page.
    return null;
  }
}

export function getToken(): string | null {
  return getSession()?.token ?? null;
}

/**
 * How long the stored session has left, in milliseconds. Negative once it has
 * lapsed, null when there is nothing stored at all.
 *
 * Reads the session directly rather than through getSession, which reports an
 * expired one as absent - the difference between "signed out" and "signed in
 * this morning and the token ran out" is the whole point of this function.
 */
export function msUntilExpiry(): number | null {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Session;
    if (!session?.token || !session.expiresAt) return null;
    const at = new Date(session.expiresAt).getTime();
    return Number.isNaN(at) ? null : at - Date.now();
  } catch {
    return null;
  }
}

/** A token good enough to start a call with. */
export function hasValidSession(): boolean {
  return getSession() !== null;
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
    // Belt and braces. Nothing in this app has ever written the session to
    // sessionStorage, but another Longview app on this origin may have, and a
    // stale one there would shadow the real session for that tab alone - which
    // is the exact shape of bug this is meant to end.
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    /* nothing to clear */
  }
}

export class LoginError extends Error {}

/** POST /login. Throws LoginError with a message safe to show the user. */
export async function signIn(email: string, password: string): Promise<Session> {
  if (!endpoints.login) {
    throw new LoginError('VITE_LOGIN_API_URL is not set in this build.');
  }

  let response: Response;
  try {
    response = await fetch(`${endpoints.login}login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // The audience the login service scopes revocation by. Sent whether or
      // not it understands it yet: an unknown field is ignored, and without it
      // signing in here evicts whoever is signed in to the console.
      body: JSON.stringify({ email, password, audience: 'virtual_inspection' }),
    });
  } catch {
    throw new LoginError('Unable to reach the server. Check your connection.');
  }

  const data = await response.json().catch(() => ({}));

  if (response.status === 401) throw new LoginError('Incorrect email or password.');
  if (response.status === 400) throw new LoginError(data?.error ?? 'Please check your input.');
  if (!response.ok) throw new LoginError('Something went wrong. Please try again.');

  const session = data as Session;
  try {
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    // Signing in still works for this tab; it just will not survive a reload.
    console.warn('[auth] session could not be stored');
  }
  return session;
}
